import {
  afterNextRender,
  afterEveryRender,
  AfterRenderRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Filter } from "../models/filter";
import { CTMSMilestone } from "../models/ctmsmilestone.model";
import { CustomMilestone } from "../models/custom-milestone.model";
import { Congress } from "../models/congress.model";
import { Publication } from "../models/publication.model";
import { Activity } from "../models/activity.model";
import { VVPMActivity } from "../models/vvpm-activity.model";
import { DetailedViewObjectService } from "../services/detailed-view-object.service";
import { FilterService } from "../services/filter.service";
import { TimelineDataService } from '../services/timeline-data.service';
import { UserService } from '../services/user.service';
import { CreateObject } from '../models/metadata';


import {
  TimestampAboveEntry,
  TimestampBelowEntry,
  TimestampComponent,
  TimestampData,
  TimestampType
} from "./timestamp/timestamp.component";
import { ZoomControlComponent } from "./zoom-control/zoom-control.component";
import { RdsDividerModule, RdsHeaderModule, RdsIconComponent, RdsTimelineModule, RdsButtonModule, RdsCheckboxModule, RdsToastService } from '@rds/angular-components';
import { CongressViewPreferenceService, CongressViewMode } from '../services/congress-view-preference.service';


export type VisibilityType = 'visible' | 'hidden';

export type TimelineData = {
  aboveTimeline: {
    ctmsMilestones: Array<CTMSMilestone>,
    customMilestones: Array<CustomMilestone>,
    congresses: Array<Congress>
  },
  belowTimeline: {
    publications: Array<Publication>,
    activities: Array<Activity>,
    vvpmActivities: Array<VVPMActivity>
  }
};

type Timestamp = {
  date: Date,
  data: TimestampData,
  type: TimestampType
};

type TimelineText = {
  date: Date,
  text: string,
  id: string,
};

@Component({
  selector: 'app-timeline',
  standalone: true,
  imports: [
    FormsModule,
    RdsDividerModule,
    RdsHeaderModule,
    TimestampComponent,
    RdsTimelineModule,
    ZoomControlComponent,
    RdsButtonModule,
    RdsIconComponent,
    RdsCheckboxModule,
],
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss']
})
export class TimelineComponent implements OnInit, OnDestroy, OnChanges {
  @Input() data!: TimelineData;
  timelineContainerDiv = viewChild.required<ElementRef<HTMLDivElement>>('timelineContainer');
  congressRow: Array<Timestamp> = [];
  milestoneRow: Array<Timestamp> = [];
  timelineTexts: Array<TimelineText> = [];
  publicationRow: Array<Timestamp> = [];
  activityRow: Array<Timestamp> = [];
  materialRow: Array<Timestamp> = [];
  currentZoom = 0.8;
  splitScreenControl = viewChild.required<ElementRef<HTMLDivElement>>('splitScreenControl');
  filters!: Filter;
  filterSubscription!: Subscription;
  private isFirstRender = true;
  editorActionsVisible: VisibilityType = 'hidden';
  isViewer: boolean = true;
  public drawerOpen: boolean = false;

  // Row visibility toggles
  congressRowVisible: boolean = true;
  milestoneRowVisible: boolean = false;
  publicationRowVisible: boolean = true;
  activityRowVisible: boolean = true;
  materialRowVisible: boolean = true;

public exportOptionsVisible: boolean = false;

  // Smooth dragging variables for main split-screen
  private dragAnimationFrame: number | null = null;
  private pendingDragUpdate: { mousePos: number } | null = null;

  // Inter-row drag variables (above-timeline)
  private interRowDragAnimationFrame: number | null = null;
  private pendingInterRowDragUpdate: { mousePos: number } | null = null;

  // Below-timeline inter-row drag variables
  private belowRowDragAnimationFrame: number | null = null;
  private pendingBelowRowDragUpdate: { mousePos: number; upperRowId: string; lowerRowId: string } | null = null;

toggleExportOptions(): void {
  this.exportOptionsVisible = !this.exportOptionsVisible;
  console.log('Export Options Visible:', this.exportOptionsVisible);
}

  exportOptions: any = {
    type: 'EXPORT_OPTIONS',
    exportActivities: false,
    exportPublications: false,
    exportCtmsMilestones: false,
    exportManualMilestones: false
  };
  
  loading: boolean = false;

  congressViewMode: CongressViewMode = 'drawer';

  constructor(
    private filterService: FilterService,
    private detailedViewObjectService: DetailedViewObjectService,
    @Inject(DOCUMENT) private document: Document,
    private userService: UserService,
    public timelineDataService: TimelineDataService,
    public toastService: RdsToastService,
    private congressViewPreference: CongressViewPreferenceService,
  ) {
    this.congressViewMode = this.congressViewPreference.mode;
    this.congressViewPreference.mode$.subscribe(mode => {
      this.congressViewMode = mode;
    });
    afterNextRender({
      read: () => {
        this.scrollMonthIntoView(this.parseIDFormat(new Date()));
        this.adaptAboveTimelineHeight();
        this.adaptBelowTimelineHeight();
        this.isFirstRender = false;
      }
    });

    afterEveryRender({
      write: () => {
        this.adaptSplitScreenControlPosition();
        this.syncTimelineScrollWidth();
        this.capRowHeights();
      }
    });

    this.userService.getUserData().then(user => {
      this.editorActionsVisible = user.isViewer ? 'hidden' : 'visible';
      this.isViewer = user.isViewer;
    });
  }

  ngOnInit() {
    this.filterSubscription = this.filterService.filterData.subscribe((filters) => {
      this.filters = filters;
      this.update();
      if (!this.isFirstRender) {
        this.playRefreshAnimation();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.filters) {
      this.update();
    }
  }

  update() {
    const parsed = this.parseTimeline(this.filters);
    this.congressRow = parsed.congressRow;
    this.milestoneRow = parsed.milestoneRow;
    this.timelineTexts = parsed.timelineTexts;
    this.publicationRow = parsed.publicationRow;
    this.activityRow = parsed.activityRow;
    this.materialRow = parsed.materialRow;

    // After filter changes (not the initial render), reset scroll and navigate
    // to the appropriate month once Angular has re-rendered the DOM.
    if (!this.isFirstRender) {
      const container = this.timelineContainerDiv()?.nativeElement;
      if (container) {
        container.scrollLeft = 0;
      }

      requestAnimationFrame(() => {
        const targetId = this.getScrollTargetId();
        this.scrollMonthIntoView(targetId);
      });
    }
  }

  /** Plays a scale-up + fade animation on the timeline container. */
  private playRefreshAnimation() {
    const container = this.timelineContainerDiv()?.nativeElement;
    if (!container) return;

    // Remove the class first to allow re-triggering if already present
    container.classList.remove('refreshing');
    // Force a reflow so the browser registers the removal before re-adding
    void container.offsetWidth;
    container.classList.add('refreshing');

    container.addEventListener('animationend', () => {
      container.classList.remove('refreshing');
    }, { once: true });
  }

  /**
   * Returns the month ID to scroll to after a filter change.
   * Prefers the current month if it falls within the filtered range,
   * otherwise falls back to the first month of the range.
   */
  private getScrollTargetId(): string {
    const now = new Date();
    const { start, end } = this.filters.timespan;

    const nowStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const filterStart = new Date(start.getFullYear(), start.getMonth(), 1);
    const filterEnd = new Date(end.getFullYear(), end.getMonth(), 1);

    if (nowStart >= filterStart && nowStart <= filterEnd) {
      return this.parseIDFormat(now);
    }
    return this.parseIDFormat(start);
  }

  onZoomChange(zoom: number) {
    const container = this.timelineContainerDiv().nativeElement;
    const scrollToCenterScreen = container.scrollLeft + container.clientWidth / 2;
    const scrollPercentage = scrollToCenterScreen / container.scrollWidth;

    this.document.getElementById('timeline-container')?.setAttribute("style", "--zoom-factor: " + zoom);
    const aboveTimelineContainer = this.document.getElementById('above-timeline-container') as HTMLElement;
    if (aboveTimelineContainer) {
      aboveTimelineContainer.style.height = aboveTimelineContainer.clientHeight * this.currentZoom / zoom + 'px';
    }
    const belowTimelineContainer = this.document.getElementById('below-timeline-container') as HTMLElement;
    if (belowTimelineContainer) {
      belowTimelineContainer.style.height = belowTimelineContainer.clientHeight * this.currentZoom / zoom + 'px';
    }
    this.currentZoom = zoom;

    container.scrollLeft = scrollPercentage * container.scrollWidth - container.clientWidth / 2;
  }

  scrollMonthIntoView(id: string) {
    this.document.getElementById(id)?.scrollIntoView({
      behavior: "instant",
      inline: "center",
      block: "nearest"
    });
  }

  adaptSplitScreenControlPosition() {
    const rdsTimelineSeparator = this.document.querySelector('.rds-timeline-event-separator');
    if (rdsTimelineSeparator) {
      const separatorBounds = rdsTimelineSeparator.getBoundingClientRect();
      this.splitScreenControl().nativeElement.style.top = (separatorBounds.top - separatorBounds.height / 2 + 1) + 'px';
    }
  }

  // Cap each row's height to the tallest column + 1 card so sections don't
  // grow beyond their content.
  private capRowHeights() {
    const rowIds = ['congress-row', 'milestone-row', 'publication-row', 'activity-row', 'material-row'];
    for (const id of rowIds) {
      const row = this.document.getElementById(id);
      if (!row) continue;

      const columns = row.querySelectorAll('app-timestamp');
      if (columns.length === 0) continue;

      let tallest = 0;
      columns.forEach(col => {
        tallest = Math.max(tallest, col.scrollHeight);
      });

      if (tallest > 0) {
        // One card height ≈ first card's height, or fallback to 120px
        const firstCard = row.querySelector('.timeline-card-container, app-publication, app-activity, app-vvpm-activity, app-ctmsmilestone, app-custom-milestone, app-congress-milestone');
        const cardHeight = firstCard ? (firstCard as HTMLElement).offsetHeight : 120;
        row.style.maxHeight = `${tallest + cardHeight}px`;
      }
    }
  }

  // Measure the content width from rds-timeline (the month labels bar) and set
  // --timeline-scroll-width so the above/below containers stretch to match.
  // We measure rds-timeline instead of container.scrollWidth to avoid a circular
  // dependency: the containers' min-width references this variable, so reading
  // scrollWidth from the container would feed back into itself and prevent shrinking.
  syncTimelineScrollWidth() {
    const container = this.timelineContainerDiv()?.nativeElement;
    if (!container) return;
    const timeline = container.querySelector('rds-timeline');
    if (timeline) {
      container.style.setProperty('--timeline-scroll-width', timeline.scrollWidth + 'px');
    }
  }


  resetSplitScreenDistribution() {
    this.document.getElementById('above-timeline-container')?.removeAttribute('style');
    this.document.getElementById('congress-row')?.removeAttribute('style');
    this.document.getElementById('milestone-row')?.removeAttribute('style');
    this.document.getElementById('below-timeline-container')?.removeAttribute('style');
    this.document.getElementById('publication-row')?.removeAttribute('style');
    this.document.getElementById('activity-row')?.removeAttribute('style');
    this.document.getElementById('material-row')?.removeAttribute('style');
  }

  splitScreenHandleDrag = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    let mousePos = 0;
    if (e instanceof MouseEvent) {
      mousePos = e.clientY;
    } else if (e instanceof TouchEvent) {
      mousePos = e.touches[0].clientY;
    }

    // Store the pending update instead of applying immediately
    this.pendingDragUpdate = { mousePos };

    // Use requestAnimationFrame for smooth updates
    if (this.dragAnimationFrame === null) {
      this.dragAnimationFrame = requestAnimationFrame(() => {
        this.performDragUpdate();
      });
    }
  }

  private performDragUpdate() {
    if (!this.pendingDragUpdate) {
      this.dragAnimationFrame = null;
      return;
    }

    const { mousePos } = this.pendingDragUpdate;
    const aboveTimelineContainer = this.document.getElementById('above-timeline-container') as HTMLElement;
    const timelineContainer = this.timelineContainerDiv().nativeElement;
    const rdsTimelineSeparator = this.document.querySelector('.rds-timeline-event-separator') as HTMLElement;

    const containerBounds = timelineContainer.getBoundingClientRect();
    const separatorBounds = rdsTimelineSeparator.getBoundingClientRect();

    // No zoom division — #above-timeline-container is not zoomed (zoom is on .row-content.data-column)
    let newHeight = mousePos - containerBounds.top - (separatorBounds.height / 2);

    if (newHeight <= 10) newHeight = 0;

    aboveTimelineContainer.style.height = `${newHeight}px`;

    this.pendingDragUpdate = null;
    this.dragAnimationFrame = null;
  }

  splitScreenHandleClick(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      e.stopPropagation();
      e.preventDefault();
    }
    this.document.getElementById('drag-handle')?.classList.add('active');
    this.timelineContainerDiv().nativeElement.classList.add('dragging');
    this.timelineContainerDiv().nativeElement.addEventListener('mousemove', this.splitScreenHandleDrag);
    this.timelineContainerDiv().nativeElement.addEventListener('touchmove', this.splitScreenHandleDrag);
    this.document.querySelector('body')?.addEventListener('mouseup', this.splitScreenHandleRelease.bind(this));
    this.document.querySelector('body')?.addEventListener('touchend', this.splitScreenHandleRelease.bind(this));
  }

  splitScreenHandleRelease() {
    this.document.getElementById('drag-handle')?.classList.remove('active');
    this.timelineContainerDiv().nativeElement.classList.remove('dragging');
    this.timelineContainerDiv().nativeElement.removeEventListener('mousemove', this.splitScreenHandleDrag);
    this.timelineContainerDiv().nativeElement.removeEventListener('touchmove', this.splitScreenHandleDrag);
    this.document.querySelector('body')?.removeEventListener('mouseup', this.splitScreenHandleRelease.bind(this));
    this.document.querySelector('body')?.removeEventListener('touchend', this.splitScreenHandleRelease.bind(this));
    
    // Clean up animation frame
    if (this.dragAnimationFrame !== null) {
      cancelAnimationFrame(this.dragAnimationFrame);
      this.dragAnimationFrame = null;
    }
    this.pendingDragUpdate = null;
  }

  // --- Row toggle logic ---

  toggleCongressRow() {
    this.congressRowVisible = !this.congressRowVisible;
    if (!this.congressRowVisible && this.milestoneRowVisible) {
      this.document.getElementById('milestone-row')?.removeAttribute('style');
    }
    if (this.congressRowVisible) {
      this.animateRowToggle('congress-row', true);
      this.document.getElementById('milestone-row')?.removeAttribute('style');
    }
    this.adaptAboveTimelineHeight();
  }

  toggleMilestoneRow() {
    this.milestoneRowVisible = !this.milestoneRowVisible;
    if (!this.milestoneRowVisible && this.congressRowVisible) {
      this.document.getElementById('congress-row')?.removeAttribute('style');
    }
    if (this.milestoneRowVisible) {
      this.animateRowToggle('milestone-row', true);
      this.document.getElementById('congress-row')?.removeAttribute('style');
    }
    this.adaptAboveTimelineHeight();
  }

  togglePublicationRow() {
    this.publicationRowVisible = !this.publicationRowVisible;
    this.animateRowToggle('publication-row', this.publicationRowVisible);
    this.adaptBelowTimelineHeight();
  }

  toggleActivityRow() {
    this.activityRowVisible = !this.activityRowVisible;
    this.animateRowToggle('activity-row', this.activityRowVisible);
    this.adaptBelowTimelineHeight();
  }

  toggleMaterialRow() {
    this.materialRowVisible = !this.materialRowVisible;
    this.animateRowToggle('material-row', this.materialRowVisible);
    this.adaptBelowTimelineHeight();
  }

  // Animate a row's expand by snapshotting height:0, measuring the target,
  // and transitioning between explicit px values.
  private animateRowToggle(rowId: string, expanding: boolean) {
    const row = this.document.getElementById(rowId);
    if (!row) return;

    row.removeAttribute('style');

    if (expanding) {
      // Row is about to expand: Angular will insert content and remove .collapsed
      // on the next change detection. We need to wait for that, then animate.
      // Use requestAnimationFrame to run after Angular updates the DOM.
      requestAnimationFrame(() => {
        const targetHeight = row.scrollHeight;
        row.style.height = '0px';
        row.style.overflow = 'hidden';
        void row.offsetHeight;
        row.style.height = targetHeight + 'px';

        const cleanup = () => {
          row.style.removeProperty('height');
          row.style.removeProperty('overflow');
          row.removeEventListener('transitionend', cleanup);
        };
        row.addEventListener('transitionend', cleanup);
      });
    }
  }

  // Adjust above-timeline container height based on which rows are visible.
  // Only uses px snapshot when collapsing to zero rows (animate to dividers-only height).
  // Expanding just updates --above-row-count and lets CSS calc handle the rest.
  private adaptAboveTimelineHeight() {
    const container = this.document.getElementById('above-timeline-container');
    if (!container) return;

    const visibleCount = (this.congressRowVisible ? 1 : 0) + (this.milestoneRowVisible ? 1 : 0);
    container.style.setProperty('--above-row-count', String(visibleCount));

    if (visibleCount === 0) {
      // Collapsing to zero — snapshot current height, then animate to dividers-only
      const currentHeight = container.getBoundingClientRect().height;
      container.style.height = currentHeight + 'px';
      void container.offsetHeight;
      const dividers = container.querySelectorAll('.section-divider');
      let dividersHeight = 0;
      dividers.forEach(d => dividersHeight += (d as HTMLElement).getBoundingClientRect().height);
      container.style.height = dividersHeight + 'px';
    } else {
      // Expanding — CSS calc with --above-row-count handles the target height
      container.style.removeProperty('height');
    }
  }

  // Adjust below-timeline container sizing based on which rows are visible.
  // Only uses px snapshot when collapsing all rows. Expanding restores flex sizing immediately.
  private adaptBelowTimelineHeight() {
    const container = this.document.getElementById('below-timeline-container');
    if (!container) return;

    const allCollapsed = !this.publicationRowVisible && !this.activityRowVisible && !this.materialRowVisible;

    if (allCollapsed) {
      // Collapsing to zero — snapshot current height, then animate to dividers-only
      const currentHeight = container.getBoundingClientRect().height;
      container.style.height = currentHeight + 'px';
      container.style.flex = '0 0 auto';
      void container.offsetHeight;
      const dividers = container.querySelectorAll('.section-divider');
      let dividersHeight = 0;
      dividers.forEach(d => dividersHeight += (d as HTMLElement).getBoundingClientRect().height);
      container.style.height = dividersHeight + 'px';
    } else {
      // Expanding — restore flex sizing, let layout handle the height
      container.style.removeProperty('height');
      container.style.removeProperty('flex');
    }
  }

  // --- Inter-row drag handle logic ---

  interRowHandleDrag = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    let mousePos = 0;
    if (e instanceof MouseEvent) {
      mousePos = e.clientY;
    } else if (e instanceof TouchEvent) {
      mousePos = e.touches[0].clientY;
    }

    this.pendingInterRowDragUpdate = { mousePos };

    if (this.interRowDragAnimationFrame === null) {
      this.interRowDragAnimationFrame = requestAnimationFrame(() => {
        this.performInterRowDragUpdate();
      });
    }
  }

  private performInterRowDragUpdate() {
    if (!this.pendingInterRowDragUpdate) {
      this.interRowDragAnimationFrame = null;
      return;
    }

    const { mousePos } = this.pendingInterRowDragUpdate;
    const congressRowEl = this.document.getElementById('congress-row') as HTMLElement;
    const milestoneRowEl = this.document.getElementById('milestone-row') as HTMLElement;
    const containerEl = this.document.getElementById('above-timeline-container') as HTMLElement;

    if (!congressRowEl || !milestoneRowEl || !containerEl) {
      this.pendingInterRowDragUpdate = null;
      this.interRowDragAnimationFrame = null;
      return;
    }

    const containerBounds = containerEl.getBoundingClientRect();
    const containerHeight = containerBounds.height;
    const interRowHandle = this.document.querySelector('#above-timeline-container .section-divider.draggable') as HTMLElement;
    const handleHeight = interRowHandle ? interRowHandle.getBoundingClientRect().height : 20;

    // No zoom division — #congress-row and #above-timeline-container are not zoomed
    let congressHeight = mousePos - containerBounds.top - (handleHeight / 2);

    // Clamp to reasonable bounds (minimum 30px each)
    const minRowHeight = 30;
    const maxCongressHeight = containerHeight - minRowHeight - handleHeight;

    if (congressHeight < minRowHeight) congressHeight = minRowHeight;
    if (congressHeight > maxCongressHeight) congressHeight = maxCongressHeight;

    congressRowEl.style.height = `${congressHeight}px`;
    congressRowEl.style.flex = 'none';
    milestoneRowEl.style.height = 'auto';
    milestoneRowEl.style.flex = '1 1 auto';

    this.pendingInterRowDragUpdate = null;
    this.interRowDragAnimationFrame = null;
  }

  interRowHandleClick(e: MouseEvent | TouchEvent) {
    if (e instanceof MouseEvent) {
      e.stopPropagation();
      e.preventDefault();
    }
    this.document.getElementById('inter-row-drag-indicator')?.classList.add('active');
    this.timelineContainerDiv().nativeElement.classList.add('dragging');
    this.timelineContainerDiv().nativeElement.addEventListener('mousemove', this.interRowHandleDrag);
    this.timelineContainerDiv().nativeElement.addEventListener('touchmove', this.interRowHandleDrag);
    this.document.querySelector('body')?.addEventListener('mouseup', this.interRowHandleRelease);
    this.document.querySelector('body')?.addEventListener('touchend', this.interRowHandleRelease);
  }

  interRowHandleRelease = () => {
    this.document.getElementById('inter-row-drag-indicator')?.classList.remove('active');
    this.timelineContainerDiv().nativeElement.classList.remove('dragging');
    this.timelineContainerDiv().nativeElement.removeEventListener('mousemove', this.interRowHandleDrag);
    this.timelineContainerDiv().nativeElement.removeEventListener('touchmove', this.interRowHandleDrag);
    this.document.querySelector('body')?.removeEventListener('mouseup', this.interRowHandleRelease);
    this.document.querySelector('body')?.removeEventListener('touchend', this.interRowHandleRelease);

    if (this.interRowDragAnimationFrame !== null) {
      cancelAnimationFrame(this.interRowDragAnimationFrame);
      this.interRowDragAnimationFrame = null;
    }
    this.pendingInterRowDragUpdate = null;
  }

  // --- Below-timeline inter-row drag logic ---

  belowRowHandleClick(e: MouseEvent | TouchEvent, upperRowId: string, lowerRowId: string) {
    if (e instanceof MouseEvent) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Store which pair of rows is being dragged
    this.pendingBelowRowDragUpdate = { mousePos: 0, upperRowId, lowerRowId };
    this.timelineContainerDiv().nativeElement.classList.add('dragging');
    this.timelineContainerDiv().nativeElement.addEventListener('mousemove', this.belowRowHandleDrag);
    this.timelineContainerDiv().nativeElement.addEventListener('touchmove', this.belowRowHandleDrag);
    this.document.querySelector('body')?.addEventListener('mouseup', this.belowRowHandleRelease);
    this.document.querySelector('body')?.addEventListener('touchend', this.belowRowHandleRelease);
  }

  belowRowHandleDrag = (e: MouseEvent | TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    let mousePos = 0;
    if (e instanceof MouseEvent) {
      mousePos = e.clientY;
    } else if (e instanceof TouchEvent) {
      mousePos = e.touches[0].clientY;
    }

    if (this.pendingBelowRowDragUpdate) {
      this.pendingBelowRowDragUpdate.mousePos = mousePos;
    }

    if (this.belowRowDragAnimationFrame === null) {
      this.belowRowDragAnimationFrame = requestAnimationFrame(() => {
        this.performBelowRowDragUpdate();
      });
    }
  }

  private performBelowRowDragUpdate() {
    if (!this.pendingBelowRowDragUpdate) {
      this.belowRowDragAnimationFrame = null;
      return;
    }

    const { mousePos, upperRowId, lowerRowId } = this.pendingBelowRowDragUpdate;
    const upperRowEl = this.document.getElementById(upperRowId) as HTMLElement;
    const lowerRowEl = this.document.getElementById(lowerRowId) as HTMLElement;
    const containerEl = this.document.getElementById('below-timeline-container') as HTMLElement;

    if (!upperRowEl || !lowerRowEl || !containerEl) {
      this.belowRowDragAnimationFrame = null;
      return;
    }

    const upperBounds = upperRowEl.getBoundingClientRect();
    const minRowHeight = 30;

    let newUpperHeight = mousePos - upperBounds.top;
    if (newUpperHeight < minRowHeight) newUpperHeight = minRowHeight;

    upperRowEl.style.height = `${newUpperHeight}px`;
    upperRowEl.style.flex = 'none';
    lowerRowEl.style.flex = '1 1 auto';

    this.belowRowDragAnimationFrame = null;
  }

  belowRowHandleRelease = () => {
    this.timelineContainerDiv().nativeElement.classList.remove('dragging');
    this.timelineContainerDiv().nativeElement.removeEventListener('mousemove', this.belowRowHandleDrag);
    this.timelineContainerDiv().nativeElement.removeEventListener('touchmove', this.belowRowHandleDrag);
    this.document.querySelector('body')?.removeEventListener('mouseup', this.belowRowHandleRelease);
    this.document.querySelector('body')?.removeEventListener('touchend', this.belowRowHandleRelease);

    if (this.belowRowDragAnimationFrame !== null) {
      cancelAnimationFrame(this.belowRowDragAnimationFrame);
      this.belowRowDragAnimationFrame = null;
    }
    this.pendingBelowRowDragUpdate = null;
  }

  createActivity() {
    this.detailedViewObjectService.nextDetailedObject(new Activity());
  }

  createCustomMilestone() {
    this.detailedViewObjectService.nextDetailedObject(new CustomMilestone());
  }

  toggleDrawer(): void {
    this.drawerOpen = !this.drawerOpen;
    console.log("toggleDrawer:", this.drawerOpen);
  }


  
  createGslide(): void {
    if (this.filters) {
      const exportObject = {
        abstractDate: new Date(),
        abstractID: 'export-options',
        toPost: (data: any) => { throw new Error("Export options toPost not implemented"); },
        type: 'EXPORT_OPTIONS',
        filters: this.filters,
        options: { ...this.exportOptions }
      } as any as CreateObject;
  
      this.detailedViewObjectService.nextDetailedObject(exportObject);
    } else {
      console.warn("No filter data available.");
      this.toastService.show('Please apply filters before creating a Google Slide.', 'Warning', {
        timeOut: 5000
      });
    }
  }
  
  
  

  parseTimeline({ timespan, entityTypes, strategicPillars, audiences }: Filter) {
    const resObj = {
      congressRow: [] as Array<Timestamp>,
      milestoneRow: [] as Array<Timestamp>,
      timelineTexts: [] as Array<TimelineText>,
      publicationRow: [] as Array<Timestamp>,
      activityRow: [] as Array<Timestamp>,
      materialRow: [] as Array<Timestamp>,
    };

    const selectedEntityTypes = entityTypes ?? [];
    const hasEntityTypeFilters = selectedEntityTypes.length > 0;

    // Determine what to show based on entity type filters
    const shouldShowPublications = !hasEntityTypeFilters || selectedEntityTypes.includes('publications');
    const shouldShowActivities = !hasEntityTypeFilters || selectedEntityTypes.includes('activities');
    const shouldShowVVPMActivities = !hasEntityTypeFilters || selectedEntityTypes.includes('vvpmActivities');
    const shouldShowCongresses = !hasEntityTypeFilters || selectedEntityTypes.includes('congresses');
    const shouldShowStandardMilestones = !hasEntityTypeFilters || selectedEntityTypes.includes('milestones');

    for (let d = new Date(timespan.start); d <= timespan.end; d.setMonth(d.getMonth() + 1)) {
      const newD = new Date(d);
      const s = this.parseStringFormat(d);
      const id = this.parseIDFormat(d);

      // Filter publications
      let currentPublications: Array<TimestampBelowEntry<Publication>> = [];
      if (shouldShowPublications) {
        currentPublications = this.filterBySpanAndMonth(d, this.data.belowTimeline.publications)
          .map(p => ({ entry: p, type: 'publication' } as TimestampBelowEntry<Publication>));
      }

      // Standard milestones are shown only when no entity type chips are active
      let currentMilestones: Array<TimestampAboveEntry<CTMSMilestone>> = [];
      let currentCustomMilestones: Array<TimestampAboveEntry<CustomMilestone>> = [];
      if (shouldShowStandardMilestones) {
        currentMilestones = this.filterBySpanAndMonth(d, this.data.aboveTimeline.ctmsMilestones)
          .map(m => ({ entry: m, type: 'ctmsmilestone' } as TimestampAboveEntry<CTMSMilestone>));
        currentCustomMilestones = this.filterBySpanAndMonth(d, this.data.aboveTimeline.customMilestones)
          .map(cm => ({ entry: cm, type: 'custom-milestone' } as TimestampAboveEntry<CustomMilestone>));
      }

      // Congress milestones — each congress can produce up to 3 entries
      let currentCongressMilestones: Array<TimestampAboveEntry<Congress>> = [];
      if (shouldShowCongresses) {
        currentCongressMilestones = this.getCongressMilestonesForMonth(d, timespan);
      }

      // Filter activities
      let currentActivities: Array<TimestampBelowEntry<Activity>> = [];
      if (shouldShowActivities) {
        currentActivities = this.filterBySpanAndMonth(d, this.data.belowTimeline.activities)
          .map(a => ({ entry: a, type: 'activity' } as TimestampBelowEntry<Activity>));
      }

      // Filter VVPM activities
      let currentVVPMActivities: Array<TimestampBelowEntry<VVPMActivity>> = [];
      if (shouldShowVVPMActivities) {
        currentVVPMActivities = this.filterBySpanAndMonth(d, this.data.belowTimeline.vvpmActivities)
          .map(va => ({ entry: va, type: 'vvpm-activity' } as TimestampBelowEntry<VVPMActivity>));
      }

      // Congress row: only congress entries, sorted by display name
      const congressSorted = currentCongressMilestones
        .toSorted((a, b) => a.entry.displayName < b.entry.displayName ? -1 : 1);
      resObj.congressRow.push({
        date: newD,
        data: congressSorted,
        type: "above",
      });

      // Milestone row: CTMS + custom milestones
      resObj.milestoneRow.push({
        date: newD,
        data: this.mergeCustomAndCTMSMilestones(currentMilestones, currentCustomMilestones),
        type: "above",
      });

      resObj.timelineTexts.push({
        date: newD,
        text: s,
        id
      });

      // Publication row
      const publicationsSorted = currentPublications
        .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
        .toSorted((a, b) => a.entry.metadata.studies.map(s => s.studyName).join(",") < b.entry.metadata.studies.map(s => s.studyName).join(",") ? -1 : 1)
        .toSorted((a, b) => a.entry.metadata.target < b.entry.metadata.target ? -1 : 1);
      resObj.publicationRow.push({
        date: newD,
        data: publicationsSorted,
        type: "below"
      });

      // Activity row
      const activitiesSorted = currentActivities
        .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
        .toSorted((a, b) => a.entry.studies.map(s => s.studyName).join(",") < b.entry.studies.map(s => s.studyName).join(",") ? -1 : 1)
        .toSorted((a, b) => a.entry.event < b.entry.event ? -1 : 1);
      resObj.activityRow.push({
        date: newD,
        data: activitiesSorted,
        type: "below"
      });

      // Material row (VVPM activities)
      const vvpmSorted = currentVVPMActivities
        .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
        .toSorted((a, b) => a.entry.name__v < b.entry.name__v ? -1 : 1);
      resObj.materialRow.push({
        date: newD,
        data: vvpmSorted,
        type: "below"
      });
    }
    return resObj;
  }

  private mergePublicationsActivitiesAndVVPM(
    publications: Array<TimestampBelowEntry<Publication>>,
    activities: Array<TimestampBelowEntry<Activity>>,
    vvpmActivities: Array<TimestampBelowEntry<VVPMActivity>>
  ) {
    const publicationsSorted = publications
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.metadata.studies.map(s => s.studyName).join(",") < b.entry.metadata.studies.map(s => s.studyName).join(",") ? -1 : 1)
      .toSorted((a, b) => a.entry.metadata.target < b.entry.metadata.target ? -1 : 1);

    const activitiesSorted = activities
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.studies.map(s => s.studyName).join(",") < b.entry.studies.map(s => s.studyName).join(",") ? -1 : 1)
      .toSorted((a, b) => a.entry.event < b.entry.event ? -1 : 1);

    const vvpmActivitiesSorted = vvpmActivities
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.name__v < b.entry.name__v ? -1 : 1);

    // Publications first, then activities, then VVPM activities at the end
    return [...publicationsSorted, ...activitiesSorted, ...vvpmActivitiesSorted];
  }

  private mergePublicationsAndActivities(
    publications: Array<TimestampBelowEntry<Publication>>,
    activities: Array<TimestampBelowEntry<Activity>>
  ) {
    const publicationsSorted = publications
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.metadata.studies.map(s => s.studyName).join(",") < b.entry.metadata.studies.map(s => s.studyName).join(",") ? -1 : 1)
      .toSorted((a, b) => a.entry.metadata.target < b.entry.metadata.target ? -1 : 1);

    const activitiesSorted = activities
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.studies.map(s => s.studyName).join(",") < b.entry.studies.map(s => s.studyName).join(",") ? -1 : 1)
      .toSorted((a, b) => a.entry.event < b.entry.event ? -1 : 1);

    return [...publicationsSorted, ...activitiesSorted];
  }

  private mergeCustomAndCTMSMilestones(
    ctmsMilestones: Array<TimestampAboveEntry<CTMSMilestone>>,
    customMilestones: Array<TimestampAboveEntry<CustomMilestone>>
  ) {
    const ctmsSorted = ctmsMilestones
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.metadata.projectName < b.entry.metadata.projectName ? -1 : 1);

    const customSorted = customMilestones
      .toSorted((a, b) => a.entry.abstractDate < b.entry.abstractDate ? -1 : 1)
      .toSorted((a, b) => a.entry.title < b.entry.title ? -1 : 1);

    return [...ctmsSorted, ...customSorted];
  }



  /**
   * Extracts congress milestone entries that fall in the given month.
   * Each congress can produce up to 3 entries (event, abstractDue, lateBreaking).
   */
  private getCongressMilestonesForMonth(
    d: Date,
    timespan: { start: Date; end: Date }
  ): Array<TimestampAboveEntry<Congress>> {
    const entries: Array<TimestampAboveEntry<Congress>> = [];
    const congresses = (this.data.aboveTimeline.congresses || []).filter(c => c.isVisible);

    for (const congress of congresses) {
      // Event milestone
      if (congress.milestones.event.startDate) {
        const eventDate = new Date(congress.milestones.event.startDate);
        if (this.dateMatchesMonth(eventDate, d, timespan)) {
          entries.push({ entry: congress, type: 'congress-event' });
        }
      }
      // Abstract due milestone
      if (congress.milestones.abstractDue.date) {
        const abstractDate = new Date(congress.milestones.abstractDue.date);
        if (this.dateMatchesMonth(abstractDate, d, timespan)) {
          entries.push({ entry: congress, type: 'congress-abstract' });
        }
      }
      // Late breaking milestone
      if (congress.milestones.lateBreaking.date) {
        const lateBreakingDate = new Date(congress.milestones.lateBreaking.date);
        if (this.dateMatchesMonth(lateBreakingDate, d, timespan)) {
          entries.push({ entry: congress, type: 'congress-latebreaking' });
        }
      }
    }

    return entries;
  }

  private dateMatchesMonth(date: Date, monthDate: Date, timespan: { start: Date; end: Date }): boolean {
    return date >= timespan.start && date <= timespan.end
      && date.getFullYear() === monthDate.getFullYear()
      && date.getMonth() === monthDate.getMonth();
  }

  filterBySpanAndMonth<T>(d: Date, source: Array<T>) {
    return source.filter(p => {
      const pDate = new Date((p as any).abstractDate);
      return pDate >= this.filters.timespan.start && pDate <= this.filters.timespan.end
        && pDate.getFullYear() == d.getFullYear() && pDate.getMonth() == d.getMonth();
    });
  }

  parseStringFormat(d: Date) {
    return d.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short'
    });
  }

  parseIDFormat(d: Date) {
    const s = this.parseStringFormat(d);
    return s.replaceAll(" ", "").toLowerCase();
  }

  logCurrentFilters(): void {
    if (this.filters) {
      console.log("aktuelle Filter:", this.filters);
    } else {
      console.warn("keine Filterdaten verfügbar");
    }
  }

  ngOnDestroy() {
    this.filterSubscription.unsubscribe();
  }
}
